import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import type { Notification } from "../lib/notifications.js";
import { NotificationLines } from "../components/NotificationLines.js";

/**
 * Single-notification detail page (ADR-0044).
 * Route: `/app/notifications/:id`
 *
 * Primary use: the fallback landing page for notification types with no
 * deep-linkable entity (e.g. `system_alert`). Notifications whose
 * `entity_type` maps to an in-app detail route navigate there directly
 * from the drawer/inbox — they don't land here in normal flow, but the
 * page is linkable by id regardless (copy-paste, bookmark, etc.).
 */

function entityLinkFor(entityType: string | null, entityId: string | null): string | null {
  if (!entityType || !entityId) return null;
  switch (entityType) {
    case "action_item":
      return `/app/action-items/${entityId}`;
    default:
      return null;
  }
}

function entityLabelFor(entityType: string | null): string {
  switch (entityType) {
    case "action_item": return "action item";
    default: return entityType ?? "entity";
  }
}

export function NotificationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const [notification, setNotification] = useState<Notification | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ notification: Notification }>(`/api/tenant/notifications/${id}`);
      setNotification(res.notification);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : (e instanceof Error ? e.message : String(e)));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  async function markRead() {
    if (!notification || notification.status !== "unread") return;
    try {
      await apiFetch(`/api/tenant/notifications/${notification.id}`, {
        method: "PATCH",
        body: { status: "read" }
      });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  useEffect(() => {
    // Auto-mark as read when the user lands on the detail page.
    if (notification && notification.status === "unread") void markRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification?.id]);

  async function dismiss() {
    if (!notification) return;
    setBusy(true);
    try {
      await apiFetch(`/api/tenant/notifications/${notification.id}`, {
        method: "PATCH",
        body: { status: "dismissed" }
      });
      toast.push({ kind: "success", message: "Dismissed." });
      nav("/app/notifications");
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  async function restore() {
    if (!notification) return;
    setBusy(true);
    try {
      await apiFetch(`/api/tenant/notifications/trash/${notification.id}/restore`, { method: "POST" });
      await load();
      toast.push({ kind: "success", message: "Restored to inbox." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageLayout title="Notification">
      {error && <ErrorState detail={error} />}
      {loading && <LoadingState />}
      {!loading && !error && notification && (
        <div className="card" style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className={`pill ${categoryPillClass(notification.category)}`}>{notification.category}</span>
            <span className="pill pillNeutral">{notification.type}</span>
            {notification.status === "dismissed" && <span className="pill pillBad">In trash</span>}
            {notification.on_behalf_of_display_name && (
              <span className="muted" style={{ fontSize: 13 }}>
                on behalf of <b>{notification.on_behalf_of_display_name}</b>
              </span>
            )}
          </div>

          <h2 style={{ margin: 0, fontSize: 20 }}>{notification.title}</h2>

          <div className="muted" style={{ fontSize: 13 }}>
            Received {new Date(notification.created_at).toLocaleString()}
            {notification.read_at && <> · Read {new Date(notification.read_at).toLocaleString()}</>}
            {notification.dismissed_at && <> · Dismissed {new Date(notification.dismissed_at).toLocaleString()}</>}
          </div>

          {notification.lines?.length > 0 ? (
            <NotificationLines lines={notification.lines} totalCount={notification.total_count} />
          ) : (
            <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.5 }}>
              {notification.body}
            </div>
          )}

          {entityLinkFor(notification.entity_type, notification.entity_id) && (
            <div>
              <Link
                to={entityLinkFor(notification.entity_type, notification.entity_id)!}
                className="btn btnPrimary"
                style={{ textDecoration: "none", display: "inline-block" }}
              >
                Open {entityLabelFor(notification.entity_type)}
              </Link>
            </div>
          )}

          <div className="actions">
            <Link to="/app/notifications" className="btn" style={{ textDecoration: "none", display: "inline-block" }}>
              Back to inbox
            </Link>
            {notification.status === "dismissed" ? (
              <button type="button" className="btn btnPrimary" onClick={() => void restore()} disabled={busy}>
                Restore to inbox
              </button>
            ) : (
              <button type="button" className="btn btnDanger" onClick={() => void dismiss()} disabled={busy}>
                Dismiss
              </button>
            )}
          </div>
        </div>
      )}
    </PageLayout>
  );
}

function categoryPillClass(category: string): string {
  switch (category) {
    case "system": return "pillWarn";
    case "digest": return "pillNeutral";
    default: return "pillNeutral";
  }
}
